import React, { useRef, useEffect, useState } from "react";
import DOMPurify from "dompurify";
import { decodeText } from "@/utils/textDecoding";
import { getVehicleCardByStyleIdSync } from "@/utils/showroomUtils";
import { ensureInventoryLoadedForHydration, getSessionInventory } from "@/utils/inventoryUtils";
import YouTubeVideoViewer from "./YouTubeVideoViewer";
import { useDealerSession } from "@/contexts/DealerSessionContext";

// --- Type Definitions ---
interface StreamingIntroMessageProps {
  displayedText: string;
  isTyping: boolean; // Show bouncing dots after intro is typed but response not complete
  isComplete: boolean;
}

interface ExtractedVideo {
  url: string;
  label: string;
}

// --- Internal Components ---
const BouncingDots: React.FC = React.memo(() => {
  return (
    <span className="inline-flex items-center ml-1">
      <span className="animate-bounce inline-block" style={{ animationDelay: "0ms" }}>
        .
      </span>
      <span className="animate-bounce inline-block" style={{ animationDelay: "150ms" }}>
        .
      </span>
      <span className="animate-bounce inline-block" style={{ animationDelay: "300ms" }}>
        .
      </span>
    </span>
  );
});
BouncingDots.displayName = "BouncingDots";

// --- Language Detection (sync, no hooks) ---
const detectCurrentLanguage = (): 'fr' | 'en' => {
  try {
    const metaTag = document.querySelector('meta[http-equiv="content-language"]') as HTMLMetaElement;
    if (metaTag && metaTag.content) {
      const langCode = metaTag.content.toLowerCase().split('-')[0];
      if (langCode === 'fr') return 'fr';
    }
  } catch {
    // Ignore errors
  }
  return 'en';
};

// --- Vehicle Link Resolution ---

/**
 * Resolve a styleId to a showroom URL
 */
const resolveStyleIdUrl = (styleId: string): string | null => {
  try {
    const vehicle = getVehicleCardByStyleIdSync(styleId);
    if (!vehicle) {
      return null;
    }
    
    const lang = detectCurrentLanguage();
    const url = lang === 'fr' ? vehicle.showroomUrlFrench : vehicle.showroomUrlEnglish;
    return url || null;
  } catch {
    return null;
  }
};

/**
 * Resolve a VIN to an inventory VDP URL
 */
const resolveVinUrl = (vin: string): string | null => {
  try {
    // Ensure inventory is loaded from cache (synchronous)
    ensureInventoryLoadedForHydration();
    
    const inventory = getSessionInventory();
    
    if (!inventory || inventory.length === 0) {
      return null;
    }
    
    // Find vehicle by VIN (check various key formats)
    const vehicle = inventory.find(v => {
      const vehicleVin = v.VIN || v.vin || v['VIN Number'] || v.vinNumber;
      return vehicleVin && vehicleVin.toUpperCase() === vin.toUpperCase();
    });
    
    if (!vehicle) {
      return null;
    }
    
    // Get VDP URL (check various key formats)
    let vdpUrl = vehicle['Vehicle Details Page (VDP)'] || 
                 vehicle['VDP URL'] || 
                 vehicle.VDP_URL || 
                 vehicle.vdpUrl || 
                 vehicle.vdp_url;
    
    if (!vdpUrl) {
      return null;
    }
    
    // For French, replace URL paths with French equivalents
    const lang = detectCurrentLanguage();
    if (lang === 'fr' && typeof vdpUrl === 'string') {
      // Handle /new/inventory/ first (more specific pattern)
      vdpUrl = vdpUrl.replace(/\/new\/inventory\//gi, '/neufs/inventaire/');
      // Then handle remaining patterns
      vdpUrl = vdpUrl.replace(/\/used\//gi, '/occasion/');
      vdpUrl = vdpUrl.replace(/\/new\//gi, '/neuf/');
    }
    
    return vdpUrl;
  } catch {
    return null;
  }
};

/**
 * Check if content still has unresolved vehicle placeholders
 */
const hasUnresolvedPlaceholders = (content: string): boolean => {
  return /\{\{(?:styleid|vin):\s*[^}]+\}\}/i.test(content);
};

/**
 * Check if a URL is on the same domain as current page
 */
const isSameDomain = (url: string): boolean => {
  try {
    const linkUrl = new URL(url, window.location.origin);
    return linkUrl.hostname === window.location.hostname;
  } catch {
    return false;
  }
};

/**
 * Get vehicle display name from inventory by VIN
 */
const getVehicleNameByVin = (vin: string): string | null => {
  try {
    ensureInventoryLoadedForHydration();
    const inventory = getSessionInventory();
    
    if (!inventory || inventory.length === 0) return null;
    
    const vehicle = inventory.find(v => {
      const vehicleVin = v.VIN || v.vin || v['VIN Number'] || v.vinNumber;
      return vehicleVin && vehicleVin.toUpperCase() === vin.toUpperCase();
    });
    
    if (!vehicle) return null;
    
    // Build display name from available fields
    const year = vehicle.Year || vehicle.year || '';
    const make = vehicle.Make || vehicle.make || '';
    const model = vehicle.Model || vehicle.model || '';
    const trim = vehicle.Trim || vehicle.trim || '';
    
    const parts = [year, make, model, trim].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  } catch {
    return null;
  }
};

/**
 * Get vehicle display name from showroom by StyleId
 */
const getVehicleNameByStyleId = (styleId: string): string | null => {
  try {
    const vehicle = getVehicleCardByStyleIdSync(styleId);
    if (!vehicle) return null;
    
    const year = vehicle.year || '';
    const make = vehicle.make || '';
    const model = vehicle.model || '';
    const trim = vehicle.trim || '';
    
    const parts = [year, make, model, trim].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : null;
  } catch {
    return null;
  }
};

/**
 * Replace [text]url patterns with clickable links
 * Pattern: [display text]https://example.com or [display text]http://example.com
 */
const replaceMarkdownStyleLinks = (content: string): string => {
  // Match [text]url pattern where url starts with http:// or https://
  // The URL ends at whitespace, newline, or end of string
  return content.replace(/\[([^\]]+)\](https?:\/\/[^\s\n<]+)/gi, (match, text, url) => {
    const trimmedUrl = url.trim();
    const trimmedText = text.trim();
    const sameDomain = isSameDomain(trimmedUrl);
    const targetAttr = sameDomain ? '' : ' target="_blank" rel="noopener noreferrer"';
    return `<a href="${trimmedUrl}"${targetAttr} class="text-blue-600 underline hover:opacity-80">${trimmedText}</a>`;
  });
};

/**
 * Check if a URL is a YouTube video link
 */
const isYouTubeUrl = (url: string): boolean => {
  return /^https?:\/\/(www\.)?(youtube\.com\/watch|youtu\.be\/)/i.test(url);
};

/**
 * Extract YouTube URLs from content and return them separately
 * Returns { cleanedContent, videos } where videos is an array of extracted YouTube URLs
 */
const extractYouTubeVideos = (content: string): { cleanedContent: string; videos: ExtractedVideo[] } => {
  const lang = detectCurrentLanguage();
  const videos: ExtractedVideo[] = [];
  
  // Pattern to match lines containing YouTube URLs with optional labels
  // Matches patterns like:
  // → Vidéo (FR) : https://youtube.com/...
  // → Vidéo (EN) #1 : https://youtube.com/...
  // Or just plain YouTube URLs
  const youtubeLinePattern = /^(→\s*)?(Vid[ée]o[^:]*:\s*)?(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s<>"{}|\\^`\[\]]+)/gim;
  
  let cleanedContent = content;
  
  // First pass: extract labeled YouTube lines (→ Vidéo : URL)
  const labeledPattern = /→\s*(Vid[ée]o[^:]*?):\s*(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s<>"{}|\\^`\[\]]+)/gi;
  cleanedContent = cleanedContent.replace(labeledPattern, (match, label, url) => {
    const cleanLabel = decodeText(label.trim());
    videos.push({ url: url.trim(), label: cleanLabel || (lang === 'fr' ? 'Vidéo' : 'Video') });
    return ''; // Remove the entire line
  });
  
  // Second pass: extract any remaining standalone YouTube URLs
  const standalonePattern = /(https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)[^\s<>"{}|\\^`\[\]]+)/gi;
  cleanedContent = cleanedContent.replace(standalonePattern, (url) => {
    // Check if this URL wasn't already extracted
    if (!videos.some(v => v.url === url)) {
      videos.push({ url, label: lang === 'fr' ? 'Vidéo' : 'Video' });
    }
    return ''; // Remove the URL
  });
  
  // Clean up any empty bullet lines that remain
  cleanedContent = cleanedContent.replace(/→\s*[\r\n]+/g, '\n');
  // Clean up multiple consecutive newlines
  cleanedContent = cleanedContent.replace(/\n{3,}/g, '\n\n');
  
  return { cleanedContent, videos };
};

/**
 * Replace plain URLs with clickable links
 * Same-domain links open in current window, external links open in new tab
 * YouTube URLs are now handled separately via extractYouTubeVideos
 * Uses placeholder approach to avoid lookbehind (not supported in older Safari)
 */
const replacePlainUrls = (content: string): string => {
  // First, temporarily replace existing anchor tags with placeholders
  // This prevents double-wrapping URLs that are already in links
  const anchors: string[] = [];
  let safeContent = content.replace(/<a\s[^>]*>[\s\S]*?<\/a>/gi, (match) => {
    anchors.push(match);
    return `__ANCHOR_PLACEHOLDER_${anchors.length - 1}__`;
  });
  
  // Now replace plain URLs with clickable links (YouTube URLs should already be extracted)
  const urlRegex = /(https?:\/\/[^\s<>"{}|\\^`\[\]]+)/gi;
  safeContent = safeContent.replace(urlRegex, (url) => {
    // Skip if it's a YouTube URL (should have been extracted already, but just in case)
    if (isYouTubeUrl(url)) {
      return ''; // Remove any remaining YouTube URLs
    }
    
    const sameDomain = isSameDomain(url);
    const targetAttr = sameDomain ? '' : ' target="_blank" rel="noopener noreferrer"';
    
    return `<a href="${url}"${targetAttr} class="text-blue-600 underline hover:opacity-80">${url}</a>`;
  });
  
  // Restore original anchor tags
  safeContent = safeContent.replace(/__ANCHOR_PLACEHOLDER_(\d+)__/g, (_, index) => anchors[parseInt(index)]);
  
  return safeContent;
};

/**
 * Replace {{styleid: X}} and {{vin: X}} patterns with clickable links
 * Same-domain links open in same tab; cross-domain links open in new tab
 * If URL can't be resolved, show vehicle name without link or remove placeholder
 */
const replaceVehicleLinks = (content: string): string => {
  const lang = detectCurrentLanguage();
  const linkText = lang === 'fr' ? 'Le voir ici' : 'View it here';
  
  // First, replace [text]url patterns
  let result = replaceMarkdownStyleLinks(content);
  
  // Replace {{styleid: X}} patterns
  result = result.replace(/\{\{styleid:\s*([^}]+)\}\}/gi, (match, styleId) => {
    const trimmedId = styleId.trim();
    const url = resolveStyleIdUrl(trimmedId);
    const vehicleName = getVehicleNameByStyleId(trimmedId);
    
    if (url) {
      const sameDomain = isSameDomain(url);
      const targetAttr = sameDomain ? '' : ' target="_blank" rel="noopener noreferrer"';
      // Use vehicle name if available, otherwise use generic link text
      const displayText = vehicleName || linkText;
      return `<a href="${url}"${targetAttr} class="text-blue-600 underline hover:opacity-80">${displayText}</a>`;
    }
    
    // If no URL but have vehicle name, show name without link
    if (vehicleName) {
      return `<strong>${vehicleName}</strong>`;
    }
    
    // Keep placeholder if data may still be loading
    return match;
  });
  
  // Replace {{vin: X}} patterns
  result = result.replace(/\{\{vin:\s*([^}]+)\}\}/gi, (match, vin) => {
    const trimmedVin = vin.trim();
    const url = resolveVinUrl(trimmedVin);
    const vehicleName = getVehicleNameByVin(trimmedVin);
    
    if (url) {
      const sameDomain = isSameDomain(url);
      const targetAttr = sameDomain ? '' : ' target="_blank" rel="noopener noreferrer"';
      // Use vehicle name if available, otherwise use generic link text
      const displayText = vehicleName || linkText;
      return `<a href="${url}"${targetAttr} class="text-blue-600 underline hover:opacity-80">${displayText}</a>`;
    }
    
    // If no URL but have vehicle name, show name without link
    if (vehicleName) {
      return `<strong>${vehicleName}</strong>`;
    }
    
    // Keep placeholder if data may still be loading
    return match;
  });
  
  return result;
};

// --- Formatting Helper (Extracted and Focused) ---

/**
 * Step 1: Cleans up dealer context markers and formats basic Markdown (bold).
 */
const cleanupAndBasicMarkdown = (content: string): string => {
  // 1. Decode text entities
  const decodedContent = decodeText(content);

  // 2. Remove dealer name [[...]] and address [[[...]]] markers
  let cleanedContent = decodedContent.replace(/\[\[\[[^\]]+\]\]\]/g, "").replace(/\[\[[^\]]+\]\]/g, "");

  // 3. Convert **text** to bold
  return cleanedContent.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
};

/**
 * Step 2: Parses lines and converts leading '→ ' bullets into a proper <ul><li> structure.
 * This handles nested newlines correctly within the line array.
 */
const parseBulletLists = (lines: string[]): string[] => {
  const parsedLines: string[] = [];
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmedLine = line.trim();
    const isBullet = /^→\s*/.test(trimmedLine);

    if (isBullet) {
      if (!inList) {
        parsedLines.push('<ul class="chat-bullet-list">');
        inList = true;
      }
      const bulletText = trimmedLine.replace(/^→\s*/, "").trim();
      parsedLines.push(`<li>${bulletText}</li>`);
    } else {
      if (inList) {
        parsedLines.push("</ul>");
        inList = false;
      }
      // Always push the line if it's not a bullet, including empty lines for spacing control
      parsedLines.push(line);
    }
  }

  // Close any open list at the end
  if (inList) {
    parsedLines.push("</ul>");
  }

  return parsedLines;
};

/**
 * Main content formatter.
 * Returns both the formatted HTML and any extracted YouTube videos
 */
const formatContentWithVideos = (content: string): { html: string; videos: ExtractedVideo[] } => {
  if (!content.trim()) return { html: "", videos: [] };

  // Step 0: Fix encoding issues (Ã© -> é, etc.) before any processing
  const decodedContent = decodeText(content);

  // Step 0.5: Extract YouTube videos before any other processing
  const { cleanedContent, videos } = extractYouTubeVideos(decodedContent);

  // Step 1: Clean and apply basic markdown
  const basicFormatted = cleanupAndBasicMarkdown(cleanedContent);

  // Step 2: Replace vehicle link placeholders ({{styleid: X}} and {{vin: X}})
  const withVehicleLinks = replaceVehicleLinks(basicFormatted);

  // Step 2.5: Replace plain URLs with clickable links
  const withPlainUrls = replacePlainUrls(withVehicleLinks);

  // Step 3: Parse bullets into lines
  const lines = withPlainUrls.split(/\r?\n/);
  const parsedLines = parseBulletLists(lines);

  // Step 4: Convert remaining newlines to <br> tags (more robust handling for paragraphs/spacing)
  let processedContent = parsedLines.join("\n");

  // Replace double newlines (paragraph) with <br><br>
  processedContent = processedContent.replace(/\n\n/g, "<br><br>");
  // Replace single remaining newlines (line break) with <br>
  processedContent = processedContent.replace(/\n/g, "<br>");

  // Step 5: Sanitize the final HTML output (allow anchor tags for vehicle links)
  const sanitizeConfig = {
    ALLOWED_TAGS: ["br", "p", "strong", "em", "u", "b", "i", "ul", "li", "div", "a"],
    ALLOWED_ATTR: ["class", "href", "target", "rel"],
  };

  return { 
    html: DOMPurify.sanitize(processedContent, sanitizeConfig),
    videos 
  };
};

/**
 * Legacy formatter for compatibility (returns only HTML string)
 */
const formatContent = (content: string): string => {
  return formatContentWithVideos(content).html;
};

// --- Main Component ---
// Uses DOM manipulation for appending to avoid re-rendering on every character
// Videos are extracted and rendered as React components for proper playback
const StreamingIntroMessage: React.FC<StreamingIntroMessageProps> = React.memo(({ displayedText, isTyping, isComplete }) => {
  const containerRef = useRef<HTMLSpanElement>(null);
  const lastLengthRef = useRef(0);
  const rawTextRef = useRef("");
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [extractedVideos, setExtractedVideos] = useState<ExtractedVideo[]>([]);
  const { sessionData } = useDealerSession();
  const videosEnabled = sessionData?.videosEnabled !== false;

  // Append new text directly to the DOM without triggering React re-render
  useEffect(() => {
    if (!containerRef.current) return;
    
    const currentLength = displayedText.length;
    const prevLength = lastLengthRef.current;
    
    if (currentLength > prevLength) {
      // New content added - update our raw text and re-format
      rawTextRef.current = displayedText;
      const { html, videos } = formatContentWithVideos(displayedText);
      containerRef.current.innerHTML = html;
      setExtractedVideos(videos);
    } else if (currentLength < prevLength || currentLength === 0) {
      // Content was reset - clear and start fresh
      rawTextRef.current = displayedText;
      if (displayedText) {
        const { html, videos } = formatContentWithVideos(displayedText);
        containerRef.current.innerHTML = html;
        setExtractedVideos(videos);
      } else {
        containerRef.current.innerHTML = "";
        setExtractedVideos([]);
      }
    }
    
    lastLengthRef.current = currentLength;
  }, [displayedText]);

  // When streaming completes, retry formatting to resolve any placeholders that were waiting for data
  useEffect(() => {
    if (!isComplete || !containerRef.current || !rawTextRef.current) return;
    
    // Check if there are unresolved placeholders
    const currentHtml = containerRef.current.innerHTML;
    if (!hasUnresolvedPlaceholders(currentHtml)) return;
    
    // Clear any existing retry timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    
    // Retry formatting after a short delay (data may have loaded)
    const retryFormat = (attempt: number) => {
      if (attempt > 3 || !containerRef.current) return;
      
      const { html, videos } = formatContentWithVideos(rawTextRef.current);
      containerRef.current.innerHTML = html;
      setExtractedVideos(videos);
      
      // If still has unresolved placeholders, retry again
      if (hasUnresolvedPlaceholders(html) && attempt < 3) {
        retryTimeoutRef.current = setTimeout(() => retryFormat(attempt + 1), 500);
      }
    };
    
    // First retry after 200ms, then 500ms intervals
    retryTimeoutRef.current = setTimeout(() => retryFormat(1), 200);
    
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [isComplete]);

  // If no text is displayed and not actively typing, return null
  if (!displayedText && !isTyping) {
    return null;
  }

  return (
    <div className="whitespace-pre-wrap break-words text-gray-800" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
      {/* Render formatted text - uses DOM manipulation for performance */}
      <span ref={containerRef} />

      {/* Show bouncing dots when streaming is active but the intro animation is finished */}
      {isTyping && !isComplete && <BouncingDots />}

      {/* Render extracted YouTube videos as embedded players */}
      {videosEnabled && extractedVideos.length > 0 && (
        <div className="mt-3 space-y-2">
          {extractedVideos.map((video, index) => (
            <YouTubeVideoViewer
              key={`${video.url}-${index}`}
              videoUrl={video.url}
              buttonText={video.label}
              detectVertical={true}
            />
          ))}
        </div>
      )}
    </div>
  );
});

StreamingIntroMessage.displayName = "StreamingIntroMessage";

export default StreamingIntroMessage;